import { createContext, useContext } from "react";
import { type DashboardPeriod } from "@/lib/dashboard-period";

/**
 * Context objects + hooks for the sales dashboard filter layer (VIB-1093, F0.2).
 *
 * Kept in a plain .ts module (no component exports) so react-refresh stays
 * happy; the provider component lives in DashboardFilterContext.tsx. Two logical
 * contexts — period and person — drive every panel at once.
 */

export interface PeriodContextValue {
  period: DashboardPeriod;
  setPeriod: (period: DashboardPeriod) => void;
  /** Oslo-local inclusive bounds, derived from `period`. */
  bounds: { fromDate: string; toDate: string };
  /** Whole weeks left in the period from now (Oslo). */
  remainingWeeks: number;
}

export interface PersonFilterContextValue {
  /** Selected sales owner's user_id, or null for the whole team. */
  selectedUserId: string | null;
  setSelectedUserId: (userId: string | null) => void;
}

export const PeriodContext = createContext<PeriodContextValue | null>(null);
export const PersonFilterContext = createContext<PersonFilterContextValue | null>(null);

export function usePeriodFilter(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriodFilter must be used within DashboardFilterProvider");
  return ctx;
}

export function usePersonFilter(): PersonFilterContextValue {
  const ctx = useContext(PersonFilterContext);
  if (!ctx) throw new Error("usePersonFilter must be used within DashboardFilterProvider");
  return ctx;
}
